import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-13095">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-13096">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-13097">
              <span className="text-xs font-bold text-indigo-800" data-id="element-13098">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-13099">
            <div className="text-center" data-id="element-13100">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-13101">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-13102">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-13103">→</div>
            <div className="text-center" data-id="element-13104">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-13105">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-13106">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-13107">
            <table className="w-full text-sm" data-id="element-13108">
              <thead data-id="element-13109">
                <tr className="bg-indigo-100/70" data-id="element-13110">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13111">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-13112">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-13113">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-13114">
                      <td className="px-3 py-1.5" data-id="element-13115">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-13116">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-13117">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-13118">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-13119">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-13120">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-13121">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-13122">
            <span className="text-2xl" data-id="element-13123">📝</span>
            <div className="text-left" data-id="element-13124">
              <div className="text-sm font-bold text-indigo-900" data-id="element-13125">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-13126">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-13127">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-13128">
            <div className="text-center" data-id="element-13129">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13130">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-13131">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-13132">→</div>
            <div className="text-center" data-id="element-13133">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-13134">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-13135">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-13136">
          <table className="w-full" data-id="element-13137">
            <thead data-id="element-13138">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-13139">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13140">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-13141">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-13142">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-13143">
                    <td className="px-4 py-3" data-id="element-13144">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-13145">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-13146">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-13147">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-13148">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-13149">
            <div className="flex items-start gap-3" data-id="element-13150">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-13151">
                <span className="text-blue-600 text-lg" data-id="element-13152">🔍</span>
              </div>
              <div data-id="element-13153">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-13154">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13155">
                  De <strong data-id="element-13156">stam</strong> krijg je door <strong data-id="element-13157">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-13158">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-13159">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13160">
              <div className="flex items-start gap-3" data-id="element-13161">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13162">
                  <span className="text-emerald-600 text-lg" data-id="element-13163">📋</span>
                </div>
                <div data-id="element-13164">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13165">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13166">
                    <p data-id="element-13167">
                      <strong data-id="element-13168">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13169">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-13170">
                      <strong data-id="element-13171">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13172">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13173">
                      <strong data-id="element-13174">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13175">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-13176">
                      <strong data-id="element-13177">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-13178">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13179">
              <div className="flex items-start gap-3" data-id="element-13180">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13181">
                  <span className="text-emerald-600 text-lg" data-id="element-13182">📋</span>
                </div>
                <div data-id="element-13183">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13184">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13185">
                    <p data-id="element-13186">
                      <strong data-id="element-13187">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-13188">
                      <strong data-id="element-13189">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13190">
                      <em data-id="element-13191">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13192">
              <div className="flex items-start gap-3" data-id="element-13193">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13194">
                  <span className="text-emerald-600 text-lg" data-id="element-13195">📋</span>
                </div>
                <div data-id="element-13196">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13197">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13198">
                    <p data-id="element-13199">
                      <strong data-id="element-13200">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-13201">
                      <strong data-id="element-13202">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-13203">
                      <em data-id="element-13204">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-13205">
            <div className="flex items-start gap-3" data-id="element-13206">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-13207">
                <span className="text-amber-600 text-lg" data-id="element-13208">💡</span>
              </div>
              <div data-id="element-13209">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-13210">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13211">
                  Bij <strong data-id="element-13212">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-13213">jij loopt</em> wordt{' '}
                  <em data-id="element-13214">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}